/* ═══════════════════════════════════════════════════════════════
   AdminService — Roles, Bill Rates, Project Types, Fee Structures,
   Billing Codes via EC2 API (cloud, in-memory cache)
   ═══════════════════════════════════════════════════════════════ */

import type { Role, BillRate, ProjectType, BillingType, ActivityCode, OverheadCategory, ExpenseType, ProjectScope, ProjectScopeAssignment, CompanyRole } from '../../shared/types';
import * as cache from './cloudCache';

export const AdminService = {
  // ─── Privilege Roles ───
  listRoles: async (): Promise<Role[]> => cache.list<Role>('roles'),
  saveRole: async (role: Role): Promise<Role> => cache.save('roles', role),
  deleteRole: async (id: string): Promise<void> => cache.remove('roles', id),

  // ─── Billing Roles ───
  billingRoles: {
    list: async (): Promise<any[]> => cache.list('billing_roles'),
    save: async (br: any): Promise<any> => cache.save('billing_roles', br),
    delete: async (id: string): Promise<void> => cache.remove('billing_roles', id),
  },

  // ─── Bill Rates ───
  listBillRates: async (): Promise<BillRate[]> => cache.list<BillRate>('bill_rates'),
  saveBillRate: async (rate: BillRate): Promise<BillRate> => cache.save('bill_rates', rate),
  deleteBillRate: async (id: string): Promise<void> => cache.remove('bill_rates', id),

  // ─── Simple Lookup Tables ───
  projectTypes: {
    list: async (): Promise<ProjectType[]> => cache.list<ProjectType>('project_types'),
    save: async (item: ProjectType): Promise<ProjectType> => cache.save('project_types', item),
    delete: async (id: string): Promise<void> => cache.remove('project_types', id),
  },
  billingTypes: {
    list: async (): Promise<BillingType[]> => cache.list<BillingType>('billing_types'),
    save: async (item: BillingType): Promise<BillingType> => cache.save('billing_types', item),
    delete: async (id: string): Promise<void> => cache.remove('billing_types', id),
  },
  activityCodes: {
    list: async (): Promise<ActivityCode[]> => cache.list<ActivityCode>('activity_codes'),
    save: async (item: ActivityCode): Promise<ActivityCode> => cache.save('activity_codes', item),
    delete: async (id: string): Promise<void> => cache.remove('activity_codes', id),
  },
  defaultPhases: {
    list: async (): Promise<any[]> => cache.list('default_phases'),
    save: async (item: any): Promise<any> => cache.save('default_phases', item),
    delete: async (id: string): Promise<void> => cache.remove('default_phases', id),
  },
  overheadCategories: {
    list: async (): Promise<OverheadCategory[]> => cache.list<OverheadCategory>('overhead_categories'),
    save: async (item: OverheadCategory): Promise<OverheadCategory> => cache.save('overhead_categories', item),
    delete: async (id: string): Promise<void> => cache.remove('overhead_categories', id),
  },
  expenseTypes: {
    list: async (): Promise<ExpenseType[]> => cache.list<ExpenseType>('expense_types'),
    save: async (item: ExpenseType): Promise<ExpenseType> => cache.save('expense_types', item),
    delete: async (id: string): Promise<void> => cache.remove('expense_types', id),
  },

  // ─── Company Roles (titles with optional badges) ───
  companyRoles: {
    list: async (): Promise<CompanyRole[]> => cache.list<CompanyRole>('company_roles'),
    save: async (item: CompanyRole): Promise<CompanyRole> => cache.save('company_roles', item),
    delete: async (id: string): Promise<void> => cache.remove('company_roles', id),
  },

  // ─── Project Scopes ───
  projectScopes: {
    list: async (): Promise<ProjectScope[]> => cache.list<ProjectScope>('project_scopes'),
    save: async (scope: ProjectScope): Promise<ProjectScope> => cache.save('project_scopes', scope),
    delete: async (id: string): Promise<void> => cache.remove('project_scopes', id),
  },
  projectScopeAssignments: {
    list: async (projectId?: string): Promise<ProjectScopeAssignment[]> => {
      if (projectId) return cache.query<ProjectScopeAssignment>('project_scope_assignments', { projectId });
      return cache.list<ProjectScopeAssignment>('project_scope_assignments');
    },
    save: async (a: ProjectScopeAssignment): Promise<ProjectScopeAssignment> => cache.save('project_scope_assignments', a),
    delete: async (id: string): Promise<void> => cache.remove('project_scope_assignments', id),
  },

  // Seed defaults — no longer needed, defaults are managed in the database directly
  seedDefaults: async (): Promise<{ seeded: string[]; errors: string[] }> => {
    return { seeded: [], errors: [] };
  },
};
